/**
 * Push on the iPhone (doc 05 §6). Taken from Book&Host 69d7046
 * web/src/core/push.ts: listeners before register, no second banner in the
 * foreground, the icon badge kept in step with unread messages.
 *
 * Not taken: the hotel's four local preference switches (MasterDay keeps
 * `notify_messages` on the server, PATCH /auth/me), the test banner and the
 * settings-screen permission helpers, which no MasterDay screen draws.
 * New: sign-out forgets this phone, so a signed-out iPhone stops ringing.
 *
 * The token's APNs environment is not sent: the server finds it on the first
 * push and keeps it (app/services/notifications.py, as Book&Host).
 */
import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { PushNotifications } from '@capacitor/push-notifications';
import { Badge } from '@capawesome/capacitor-badge';

import { api } from './api';

type PushTapHandler = (path: string) => void;
let tapHandler: PushTapHandler | null = null;
let currentToken: string | null = null;

/** Where a tapped push leads. The server sends `url` = `/messages/{client_id}`. */
export function pushTargetPath(data: Record<string, unknown> | undefined): string | null {
  const nested = data?.data as Record<string, unknown> | undefined;
  const url = data?.url ?? nested?.url;
  // Only a path inside the app: never a scheme or another site.
  return typeof url === 'string' && url.startsWith('/') && !url.startsWith('//') ? url : null;
}

export function setPushTapHandler(handler: PushTapHandler): void {
  tapHandler = handler;
}

function openTapped(data: Record<string, unknown> | undefined): void {
  const path = pushTargetPath(data);
  if (path && tapHandler) tapHandler(path);
}

/** Book&Host's badge sync: the server's `aps.badge` is right only when the push
 *  is sent; every change after that belongs to the app. At zero the delivered
 *  notifications are cleared too. Never throws; no-op off-device. */
export async function syncNotificationBadge(unreadCount: number): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  const count = Number.isFinite(unreadCount) ? Math.max(0, Math.trunc(unreadCount)) : 0;

  try {
    if (count === 0) {
      await Badge.clear();
      await PushNotifications.removeAllDeliveredNotifications();
      return;
    }
    await Badge.set({ count });
  } catch (err) {
    console.warn('[Push] Failed to sync notification badge:', err);
  }
}

let listenersAttached = false;

/** Attaches every push listener exactly once, before register(): Apple can
 *  return the token in under a millisecond, and a token that arrives with
 *  nothing listening is lost silently (Book&Host). */
async function attachPushListeners(): Promise<void> {
  if (listenersAttached) return;
  listenersAttached = true;

  await PushNotifications.addListener('registration', async (token) => {
    currentToken = token.value;
    try {
      // The server sends 'ios' tokens through APNs and 'android' ones through FCM.
      await api.registerDeviceToken(token.value, Capacitor.getPlatform() === 'android' ? 'android' : 'ios');
    } catch (err) {
      console.error('[Push] Failed to register device token with backend:', err);
    }
  });

  await PushNotifications.addListener('registrationError', (err) => {
    console.error('[Push] Push notification registration error:', err.error);
  });

  // Foreground arrival. Deliberately draws nothing: presentationOptions in
  // capacitor.config.ts already has iOS draw the banner, and drawing one here
  // too stacked two identical banners on Book&Host's owner's screen.
  await PushNotifications.addListener('pushNotificationReceived', () => {});

  // A tap on the lock screen or a banner.
  await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    openTapped(action.notification.data as Record<string, unknown> | undefined);
  });

  await LocalNotifications.addListener('localNotificationActionPerformed', (action) => {
    openTapped(action.notification.extra as Record<string, unknown> | undefined);
  });
}

/** Called once she is signed in: asks iOS for permission the first time, then
 *  registers this phone. A plain browser or the PWA skips it entirely. */
export async function initPushNotifications(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  try {
    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
      await LocalNotifications.requestPermissions();
    }

    if (permStatus.receive !== 'granted') {
      console.warn('[Push] Push notification permission not granted:', permStatus.receive);
      return;
    }

    // Android: without our own channel, Firebase posts to its fallback one at
    // IMPORTANCE_DEFAULT, which never pops up. 4 is IMPORTANCE_HIGH ("appears
    // as a heads-up notification"). AndroidManifest.xml names this id as the
    // default; the name matches the iPhone's grouping (docs/android/ANDROID.md §3).
    if (Capacitor.getPlatform() === 'android') {
      await PushNotifications.createChannel({ id: 'messages', name: 'MasterDay', importance: 4 });
    }

    await attachPushListeners();
    await PushNotifications.register();
  } catch (err) {
    console.warn('[Push] Capacitor push initialization skipped or failed:', err);
  }
}

/** Before sign-out: this phone stops receiving her salon's alerts. Never throws. */
export async function forgetThisDevice(): Promise<void> {
  if (!Capacitor.isNativePlatform() || !currentToken) return;
  try {
    await api.deleteDeviceToken(currentToken);
  } catch (err) {
    console.warn('[Push] Failed to delete device token:', err);
  }
  currentToken = null;
}

/** Forgets the token on the device only: after the account is deleted the
 *  server has already dropped it, and would refuse the DELETE (D-89). */
export function forgetPushToken(): void {
  currentToken = null;
}
